import React, {useState} from 'react';
import PropTypes from 'prop-types';

import {Box, Grid, Stack, useMediaQuery, useTheme} from '@mui/material';

import {useCanvasSave} from 'canvas/useCanvas';
import {useCanvasStore} from 'canvas/zustand';
import {useAnalytics} from 'hooks';
import {useLockerRoomStore} from 'module/lockerRoom/zustand';
import {useSheet} from 'module/sheet/useSheet';
import {useUserUtils} from 'module/user/useUser';
import {useLayoutStore} from 'store';
import {usePrefsStore} from 'store/prefs';

import {ColorTool, DrawingTool, PlayerTool} from './components';
import {Flyout, ToolbarButton} from './components';

const styles = {
    root: {
        backgroundColor: 'grey.darkest',
        position: 'relative',
        justifyContent: 'space-between',
        height: {
            mobile: '50px',
            desktop: '70px',
        },
        width: '100%',
        flexGrow: 1,
        '& > *': {
            height: {
                mobile: 50,
                desktop: 70,
            },
        },
    },
    container: {
        height: {
            mobile: '50px',
            desktop: '70px',
        },
        width: '100%',
        flexGrow: 1,
    },
    toolbarPlaceHolder: {
        width: {
            mobile: '12.5%',
            desktop: '7.692307%',
        },
    },
};

//const defaultIconColor = 'black.main';

const defaultToolOptions = {
    color: 'black.main'
};

const Toolbar = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [flyout, setFlyout] = useState(undefined);

    const tool = usePrefsStore((state) => state.tool);
    const setTool = usePrefsStore((state) => state.setTool);
    const color = usePrefsStore((state) => state.color);
    const prefs = usePrefsStore((state) => state.prefs);

    const setShowTimeline = useLayoutStore((state) => state.setShowTimeline);
    const setShowLineup = useLayoutStore((state) => state.setShowLineup);
    const setShowLogin = useLayoutStore((state) => state.setShowLogin);
    const {isAuthenticated} = useUserUtils();
    
    const active = useLockerRoomStore((state) => state.active);
    const editor = useLockerRoomStore((state) => state.editor);
    const dirty = useCanvasStore((state) => state.dirty);
    const background = usePrefsStore((state) => state.background);
    
    const {selectedSheet: sheet, getNumFrames} = useSheet();
    const numFrames = getNumFrames();
    
    const {save: saveCanvas} = useCanvasSave();
    const sendAnalytics = useAnalytics();

    const toolSelected = (t) => {
        console.log('toolSelected', t, tool);
        if (tool !== t) {
            sendAnalytics('tool', t);
            setTool(t);
        }

        setTimeout(() => setFlyout(undefined), 100);
    };

    const showFlyout = (tool, anchor, children) => {
        setFlyout({tool, anchor, children});
    };

    const closeFlyout = () => {
        setFlyout(undefined);
    };

    const toggleFlyout = (tool, element, e) => {
        if (flyout?.tool === tool) {
            closeFlyout();
        } else {
            showFlyout(tool, e.currentTarget, element);
        }
    };

    const toolWithFlyout = (newTool, element, e) => {
        // console.log(tool, newTool);
        if (tool === newTool) {
            toggleFlyout(newTool, element, e);
        } else {
            toolSelected(newTool);
        }
    };

    const handleOpenTimeline = async () => {
        try {
            if (dirty) {
                await saveCanvas(sheet, {background});
            }

            setShowTimeline(true);

        } catch (e) {
            console.error(e);
            return;

        }

    };

    if (active && !editor) {
        return null;
    }

    return <Grid
        container
        direction="row"
        sx={styles.root}
    >
        <Grid sx={{width:'100%'}}>
            {flyout !== undefined &&
                <Flyout open={true} onClose={closeFlyout} anchorEl={flyout?.anchor}>{flyout.children}</Flyout>}

            <Stack direction="row" sx={styles.container}>
                {/* 1. Select-Tool */}
                <ToolbarButton
                    data-tour="tool-select"
                    icon="select"
                    dropdown={false}
                    selected={tool === 'select'}
                    onClick={() => toolSelected('select')}/>

                {/* 2. Zeichen-Tool */}
                <ToolbarButton
                    data-tour="tool-draw"
                    icon={prefs.draw[prefs.draw.type]}
                    dropdown={true}
                    selected={tool === 'draw'}
                    onClick={(e) => toolWithFlyout('draw', <DrawingTool onSelect={t => toolSelected(t)}/>, e)}
                />

                {/* 3. Text-Tool */}
                <ToolbarButton
                    data-tour="tool-text"
                    icon="text"
                    dropdown={true}
                    selected={tool === 'text'}
                    onClick={(e) => toolWithFlyout('text', <ColorTool standalone onSelect={closeFlyout}/>, e)}
                />

                {/* 4. Player-Tool */}
                <ToolbarButton
                    data-tour="tool-player"
                    icon={prefs.player?.type || 'player'} dropdown={true} selected={tool === 'player'}
                    onClick={(e) => toolWithFlyout('player', <PlayerTool
                        onSelect={() => toolSelected('player')}/>, e)}
                />

                {/* 5. Lineup-Tool */}
                <ToolbarButton
                    data-tour="tool-lineup"
                    icon="lineup"
                    selected={tool === 'lineup'}
                    dropdown={false}
                    locked={!isAuthenticated}
                    onClick={() => {
                        toolSelected('lineup', defaultToolOptions);
                        closeFlyout();
                        isAuthenticated ? setShowLineup(true) : setShowLogin(true);
                    }}
                />

                {/* 6. Color-Tool */}
                <ToolbarButton
                    data-tour="tool-color"
                    icon="colorQuadrat" dropdown={true} color={color.value1}
                    selected={flyout?.tool === 'color'}
                    onClick={(e) => toggleFlyout('color', <ColorTool standalone onSelect={closeFlyout}/>, e)}
                />

                {/* 7. Placeholder */}
                {isMobile && <Box sx={styles.toolbarPlaceHolder}/>}

                {!isMobile && <>
                    <Box sx={styles.toolbarPlaceHolder}/>
                    <Box sx={styles.toolbarPlaceHolder}/>
                    <Box sx={styles.toolbarPlaceHolder}/>
                    <Box sx={styles.toolbarPlaceHolder}/>
                    <Box sx={styles.toolbarPlaceHolder}/>
                    <Box sx={styles.toolbarPlaceHolder}/>
                </>}

                {/* 8. Spielzug */}
                <ToolbarButton
                    data-tour="tool-timeline"
                    icon="timeline" badge={numFrames > 0 ? numFrames : undefined}
                    onClick={handleOpenTimeline}/>

            </Stack>
        </Grid>
    </Grid>;
};

Toolbar.propTypes = {
    onAction: PropTypes.func,
};

export default Toolbar;
